import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import { Spinner } from 'reactstrap';

import Avatar from 'src/componentes/avatar';
import Input from 'src/componentes/input';
import LucideIcon from 'src/componentes/lucide-icon';
import { SophieIcon } from 'src/componentes/icons';
import ContentManager from 'src/componentes/content-manager';
import Confirm from 'src/componentes/NewConfirm';
import useAuth from 'src/hooks/useAuth';
import useChatIA from 'src/hooks/UseChatIA';

import './ChatbotSuporte.css';

const URL_REGEX = /(https?:\/\/[^\s<>[\]()]+)/g;

function linkifyText(text) {
  if (!text) return text;
  return text.replace(URL_REGEX, (url, _match, offset, full) => {
    const isAlreadyMarkdownLink =
      full.slice(Math.max(0, offset - 2), offset) === '](';
    return isAlreadyMarkdownLink ? url : `[${url}](${url})`;
  });
}

function ChatbotSuporte({
  user,
  prompt,
  title,
  interactionType,
  usePromptButtons = false,
  listButtons = [],
  customGreetins = null,
  dontShowFeedbackSophie = false,
  onClose,
  remove,
}) {
  const intl = useIntl();
  const { isAuthenticated } = useAuth();
  const bodyRef = useRef(null);
  const chatRef = useRef(null);
  const dragRef = useRef({
    startX: 0,
    startOffsetX: 0,
    currentOffset: 0,
    moved: false,
  });
  const [minimized, setMinimized] = useState(false);
  const [offsetX, setOffsetX] = useState(0);

  const {
    messages,
    isReady,
    isStreaming,
    promptButtonsVisible,
    newMessage,
    setNewMessage,
    sendMessage,
    handlePromptButtonClick,
    copiedMessageIndex,
    copyMessage,
    requestClose,
  } = useChatIA({
    user,
    prompt,
    interactionType,
    usePromptButtons,
    listButtons,
    dontShowFeedbackSophie,
  });

  useEffect(() => {
    if (minimized) return;
    bodyRef.current?.scrollTo({
      top: bodyRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, minimized]);

  const handleClose = () => {
    requestClose();
    onClose?.();
    remove?.();
  };

  const handleCloseClick = (event) => {
    event.stopPropagation();
    ContentManager.addContent(
      <Confirm
        cancelColor="primary"
        showCancel={true}
        labelConfirm={intl.formatMessage({ id: 'label.fechar' })}
        primaryMessage={intl.formatMessage({
          id: 'voceEstaFechandoModalSophie',
        })}
        secondaryMessage={intl.formatMessage({
          id: 'historicoSophieSeraPerdido',
        })}
        emphasysMessage={intl.formatMessage({
          id: 'desejaFechaloMesmoAssim',
        })}
        onSaved={handleClose}
      />
    );
  };

  const handleToggleMinimize = (event) => {
    event.stopPropagation();
    setMinimized((prev) => !prev);
  };

  const handleKeyDown = (event) => {
    if (event.keyCode === 13 && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const clampOffsetX = (value) => {
    const el = chatRef.current;
    if (!el) return value;
    const rightGap = parseFloat(getComputedStyle(el).right) || 0;
    const maxOffset = rightGap;
    const minOffset = -(window.innerWidth - el.offsetWidth - rightGap);
    return Math.min(maxOffset, Math.max(minOffset, value));
  };

  useEffect(() => {
    if (minimized) return;
    setOffsetX((prev) => clampOffsetX(prev));
  }, [minimized]);

  useEffect(() => {
    if (isAuthenticated) return;
    onClose?.();
    remove?.();
  }, [isAuthenticated, onClose, remove]);

  const handleDragMove = (event) => {
    const drag = dragRef.current;
    const delta = event.clientX - drag.startX;
    if (Math.abs(delta) > 3) drag.moved = true;

    const nextOffset = clampOffsetX(drag.startOffsetX + delta);
    drag.currentOffset = nextOffset;
    if (chatRef.current) {
      chatRef.current.style.transform = `translateX(${nextOffset}px)`;
    }
  };

  const handleDragEnd = () => {
    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', handleDragEnd);
    if (dragRef.current.moved) setOffsetX(dragRef.current.currentOffset);
  };

  const handleDragStart = (event) => {
    if (window.innerWidth <= 480) return;
    if (event.target.closest('button')) return;
    event.preventDefault();

    dragRef.current = {
      startX: event.clientX,
      startOffsetX: offsetX,
      currentOffset: offsetX,
      moved: false,
    };
    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
  };

  const inputDisabled = (!isReady || isStreaming) && !promptButtonsVisible;
  const showEmptyState = isReady && !messages.some((msg) => msg.text !== '');

  if (minimized) {
    return (
      <div
        ref={chatRef}
        className="chatbot-suporte chatbot-suporte--minimized"
        style={{ transform: `translateX(${offsetX}px)` }}
        onMouseDown={handleDragStart}
      >
        <button
          type="button"
          className="chatbot-suporte__mini-restore"
          aria-label="Abrir chat"
          onClick={() => setMinimized(false)}
        >
          <SophieIcon color="#fff" size={18} />
        </button>
        <button
          type="button"
          className="chatbot-suporte__mini-close"
          aria-label="Fechar"
          onClick={handleCloseClick}
        >
          <LucideIcon icon="X" size={16} />
        </button>
      </div>
    );
  }

  return (
    <div
      ref={chatRef}
      className="chatbot-suporte"
      style={{ transform: `translateX(${offsetX}px)` }}
    >
      <div className="chatbot-suporte__header" onMouseDown={handleDragStart}>
        <div className="chatbot-suporte__title">
          <SophieIcon color="#fff" size={18} />
          <span>{title}</span>
        </div>
        <div className="chatbot-suporte__actions">
          <button
            type="button"
            aria-label="Minimizar"
            onClick={handleToggleMinimize}
          >
            <LucideIcon icon="Minus" size={16} />
          </button>
          <button type="button" aria-label="Fechar" onClick={handleCloseClick}>
            <LucideIcon icon="X" size={16} />
          </button>
        </div>
      </div>

      <div className="chatbot-suporte__body" ref={bodyRef}>
        {!isReady ? (
          <div className="chatbot-suporte__loading">
            <Spinner size="sm" color="primary" />
          </div>
        ) : showEmptyState && customGreetins ? (
          <div className="chatbot-suporte__greetings">
            <SophieIcon size={22} />
            <p>{customGreetins}</p>
          </div>
        ) : (
          messages
            .filter((msg) => msg.isVisible)
            .map((msg, index) => (
              <div
                key={index}
                className={`chatbot-suporte__message ${
                  msg.isUser ? 'is-user' : 'is-bot'
                }`}
              >
                {msg.isUser ? (
                  <Avatar
                    size={24}
                    label={user?.nome}
                    foto={user?.fotoArquivo}
                    showLabel={false}
                  />
                ) : (
                  <SophieIcon size={16} />
                )}
                <div className="chatbot-suporte__message-text">
                  <div>
                    <ReactMarkdown
                      components={{
                        a: ({ node, ...props }) => (
                          <a
                            {...props}
                            target="_blank"
                            rel="noreferrer noopener"
                          />
                        ),
                      }}
                    >
                      {linkifyText(msg.text)}
                    </ReactMarkdown>
                  </div>
                  {!msg.isUser && (
                    <LucideIcon
                      size={14}
                      className="chatbot-suporte__copy-icon"
                      icon={copiedMessageIndex === index ? 'Check' : 'Copy'}
                      onClick={() => copyMessage(msg.text, index)}
                    />
                  )}
                </div>
              </div>
            ))
        )}
      </div>

      {promptButtonsVisible && (
        <div className="chatbot-suporte__prompt-buttons">
          {listButtons.map((btn, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() =>
                handlePromptButtonClick(btn.prompt, btn.nameButton)
              }
            >
              {btn.nameButton}
            </button>
          ))}
        </div>
      )}

      <div className="chatbot-suporte__footer">
        <div className="chatbot-suporte__input-row">
          <div
            className="chatbot-suporte__input-wrapper"
            onKeyDown={handleKeyDown}
          >
            <Input
              disabled={inputDisabled}
              formStyle={{ width: '100%' }}
              type="textarea"
              rows={2}
              maxLength={1000}
              placeholder={intl.formatMessage({
                id: 'digiteNovasInstrucoesAqui',
              })}
              model={{
                value: newMessage,
                requestChange: setNewMessage,
              }}
            />
          </div>

          {(isReady || promptButtonsVisible) &&
            (!isStreaming || promptButtonsVisible ? (
              <LucideIcon
                icon="Send"
                size={18}
                className="text-primary"
                onClick={sendMessage}
              />
            ) : (
              <Spinner size="sm" color="primary" />
            ))}
        </div>
        <span className="chatbot-suporte__disclaimer">
          {intl.formatMessage({ id: 'avisoSophie' })}
        </span>
      </div>
    </div>
  );
}

export default ChatbotSuporte;
